import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import {
  App,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
} from 'antd';
import { styled } from 'styled-components';
import { useLocations } from '@/api/Locations';
import {
  useSavePortCall,
  useVesselAgents,
  useVesselBerths,
} from '@/api/Vessel';
import { useTranslate } from '@/translations';
import { VesselLookup, VesselPortCall } from '@/types';

type EditPortCallModalProps = {
  portCall?: VesselPortCall;
  onClose: () => void;
};

type PortCallFormValues = {
  siteId: number;
  destinationCompoundId?: number;
  agentId?: number;
  plannedBerthId?: number;
  actualBerthId?: number;
  eta?: dayjs.Dayjs;
  ata?: dayjs.Dayjs;
  etd?: dayjs.Dayjs;
  atd?: dayjs.Dayjs;
  plannedVehicleQty?: number;
  notes?: string;
};

const pendingMenu = (pending?: boolean) =>
  pending ? <Spin size="small" /> : undefined;

const Fields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const toDate = (value?: string) => (value ? dayjs(value) : undefined);

const lookupOptions = (rows: VesselLookup[] | undefined, selectedId?: number) =>
  (rows ?? [])
    .filter((row) => row.isActive || row.id === selectedId)
    .map((row) => ({ value: row.id, label: row.name }));

export const EditPortCallModal = ({
  portCall,
  onClose,
}: EditPortCallModalProps) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [form] = Form.useForm<PortCallFormValues>();
  const open = Boolean(portCall);
  const savePortCall = useSavePortCall();
  const agents = useVesselAgents(open);
  const berths = useVesselBerths(open);
  const locations = useLocations({ enabled: open });
  const locationsLoading = locations.isPending || locations.isFetching;
  const locationOptions = (locations.data ?? []).map((location) => ({
    value: location.id,
    label: location.desc || location.code,
  }));

  useEffect(() => {
    if (!portCall) return;
    form.setFieldsValue({
      siteId: portCall.siteId,
      destinationCompoundId: portCall.destinationCompoundId,
      agentId: portCall.agentId,
      plannedBerthId: portCall.plannedBerthId,
      actualBerthId: portCall.actualBerthId,
      eta: toDate(portCall.eta),
      ata: toDate(portCall.ata),
      etd: toDate(portCall.etd),
      atd: toDate(portCall.atd),
      plannedVehicleQty: portCall.plannedVehicleQty,
      notes: portCall.notes,
    });
  }, [form, portCall]);

  const close = () => {
    form.resetFields();
    onClose();
  };

  const save = async (values: PortCallFormValues) => {
    if (!portCall) return;
    const result = await savePortCall.mutateAsync({
      id: portCall.id,
      vesselId: portCall.vesselId,
      vesselJourneyId: portCall.vesselJourneyId,
      voyage: portCall.voyage,
      siteId: values.siteId,
      destinationCompoundId: values.destinationCompoundId,
      agentId: values.agentId,
      plannedBerthId: values.plannedBerthId,
      actualBerthId: values.actualBerthId,
      eta: values.eta?.toISOString(),
      ata: values.ata?.toISOString(),
      etd: values.etd?.toISOString(),
      atd: values.atd?.toISOString(),
      plannedVehicleQty: values.plannedVehicleQty,
      notes: values.notes,
    });
    if (!result.success || !result.data) {
      message.error(result.message || t('generalError'));
      return;
    }
    message.success(
      portCall.status === 'Upcoming' && result.data.status === 'InPort'
        ? t('portCallMovedInPort')
        : t('saveChanges'),
    );
    close();
  };

  return (
    <Modal
      title={t('editPortCall')}
      open={open}
      width={760}
      onCancel={close}
      destroyOnHidden
      okText={t('saveChanges')}
      confirmLoading={savePortCall.isPending}
      onOk={() => form.submit()}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => void save(values)}
      >
        <Fields>
          <Form.Item label={t('vessel')}>
            <Input
              disabled
              value={
                portCall?.imo
                  ? `${portCall.vesselName} (${portCall.imo})`
                  : portCall?.vesselName
              }
            />
          </Form.Item>
          <Form.Item label={t('voyage')}>
            <Input disabled value={portCall?.voyage} />
          </Form.Item>
          <Form.Item
            name="siteId"
            label={t('siteTerminal')}
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={locationOptions}
              loading={locationsLoading}
              notFoundContent={pendingMenu(locationsLoading)}
            />
          </Form.Item>
          <Form.Item
            name="destinationCompoundId"
            label={t('destinationCompound')}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={locationOptions}
              loading={locationsLoading}
              notFoundContent={pendingMenu(locationsLoading)}
            />
          </Form.Item>
          <Form.Item name="agentId" label={t('agent')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('chooseAgent')}
              options={lookupOptions(agents.data, portCall?.agentId)}
              loading={agents.isPending || agents.isFetching}
              notFoundContent={pendingMenu(
                agents.isPending || agents.isFetching,
              )}
            />
          </Form.Item>
          <Form.Item name="plannedBerthId" label={t('berth')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('chooseBerth')}
              options={lookupOptions(berths.data, portCall?.plannedBerthId)}
              loading={berths.isPending || berths.isFetching}
              notFoundContent={pendingMenu(
                berths.isPending || berths.isFetching,
              )}
            />
          </Form.Item>
          <Form.Item name="actualBerthId" label={t('actualBerth')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('chooseBerth')}
              options={lookupOptions(berths.data, portCall?.actualBerthId)}
              loading={berths.isPending || berths.isFetching}
              notFoundContent={pendingMenu(
                berths.isPending || berths.isFetching,
              )}
            />
          </Form.Item>
          <Form.Item name="plannedVehicleQty" label={t('plannedVehicles')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="eta"
            label={t('etaUtc')}
            rules={[{ required: true }]}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
            />
          </Form.Item>
          <Form.Item
            name="ata"
            label={t('ata')}
            extra={
              portCall?.status === 'Upcoming' ? t('ataStatusHint') : undefined
            }
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
            />
          </Form.Item>
          <Form.Item name="etd" label={t('etd')}>
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
            />
          </Form.Item>
          <Form.Item name="atd" label={t('atd')}>
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
            />
          </Form.Item>
        </Fields>
        <Form.Item name="notes" label={t('notes')}>
          <Input.TextArea rows={3} maxLength={2000} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
